import { create } from "zustand";
import { StatusMsg } from "utils";
import { AppLog } from "services";
import { DialogMessageProps } from "utils/enum/dialogMessageProps";
import { DialogConfirmProps } from "utils/enum/dialogConfirmProps";
import { DialogUploadEditPostContentProps, DialogUploadPostContentProps, UploadPostContentType } from "utils/enum/UploadPostContentType";
import { Service } from "utils/services";

type Theme = "dark" | "light";

interface DialogPostModalProps {
  isOpen: boolean;
  postId: string | null;
  tmpRouteUrl: string | null;
}

interface DialogWelcomeProps {
  isOpen: boolean;
}

interface DialogViewStoryModalProps {
  isOpen: boolean;
  userId: string | null;
  tmpRouteUrl: string | null;
}

interface AppState {
  // * ==== Dialog Message and Confirm ==== * //
  dialogMessage: DialogMessageProps;
  dialogConfirm: DialogConfirmProps;
  hideDialogMessage: () => Promise<void>;
  showDialogMessage: (props?: { status?: StatusMsg; title?: string; message?: string; barrierDismissible?: boolean }) => Promise<void>;
  hideDialogConfirm: () => Promise<void>;
  showDialogConfirm: (props?: {
    status?: StatusMsg;
    title?: string;
    message?: string;
    barrierDismissible?: boolean;
    data?: any;
    onSubmit: (data?: any) => void;
    titileButtonSubmit?: string;
    titileButtonCancel?: string;
  }) => Promise<void>;

  // * ==== Sound Controll ==== * //
  isMuted: boolean;
  volume: number;
  toggleMute: () => void;
  setGlobalVolume: (newVolume: number) => void;

  // * ==== Theme ==== * //
  theme: Theme;
  toggleTheme: () => void;

  // * ==== Upload Post Content ==== * //
  dialogUploadPostContent: DialogUploadPostContentProps;
  showDialogUploadPostContent: (uploadPostContentType: UploadPostContentType) => Promise<void>;
  hideDialogUploadPostContent: () => Promise<void>;

  // * ==== Upload Edit Post Content ==== * //
  dialogUploadEditPostContent: DialogUploadEditPostContentProps;
  showDialogUploadEditPostContent: (postEdit: any, onSuccess: (post: any) => void) => Promise<void>;
  hideDialogUploadEditPostContent: () => Promise<void>;

  // * ==== Dialog Post Modal ==== * //
  dialogPostModal: DialogPostModalProps;
  onOpenDialogPostModal: (postId: string, tmpRouteUrl: string) => Promise<void>;
  onCloseDialogPostModal: () => Promise<void>;

  // * ==== Dialog Welcome ==== * //
  dialogWelcome: DialogWelcomeProps;
  onOpenDialogWelcome: () => Promise<void>;
  onCloseDialogWelcome: () => Promise<void>;

  // * ==== Dialog Welcome ==== * //
  dialogViewStoryModal: DialogViewStoryModalProps;
  onOpenDialogViewStoryModal: (userId: string, tmpRouteUrl: string) => Promise<void>;
  onCloseDialogViewStoryModal: () => Promise<void>;
}

const getStoredValue = <T>(key: string, defaultValue: T): T => {
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error);
      return defaultValue;
    }
  }
  return defaultValue;
};

const getThemeValue = (): Theme => {
  let theme: Theme = "light";

  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("theme");
    theme = stored === "dark" || stored === "light" ? (stored as Theme) : "light";
  }

  if (typeof document !== "undefined") {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
  }

  return theme;
};

const appStore = create<AppState>((set, get) => ({
  // * ==== Dialog Message and Confirm ==== * //
  dialogMessage: {
    isOpen: false,
    title: "Information",
    message: "",
    status: StatusMsg.info,
    barrierDismissible: true,
  },

  dialogConfirm: {
    isOpen: false,
    title: "Confirmation",
    message: "",
    barrierDismissible: false,
    data: null,
    onSubmit(data) {},
    titileButtonSubmit: "Yes, Continue",
    titileButtonCancel: "No, Cancel",
  },

  showDialogMessage: async ({
    status = StatusMsg.info,
    title = "Information",
    message = "",
    barrierDismissible = true,
  }: // titileButtonSubmit:"Ok",
  Partial<{
    status: StatusMsg;
    title: string;
    message: string;
    barrierDismissible: boolean;
    // titileButtonSubmit:string;
  }> = {}) => {
    try {
      const { dialogMessage } = get();
      let tmpDialogShow = { ...dialogMessage };

      tmpDialogShow.isOpen = true;
      tmpDialogShow.title = title;
      tmpDialogShow.message = message;
      tmpDialogShow.status = status;
      tmpDialogShow.barrierDismissible = barrierDismissible;

      set({
        dialogMessage: tmpDialogShow,
      });
    } catch (error) {
      AppLog.show({
        message: `[showDialogMessage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  hideDialogMessage: async () => {
    try {
      set({
        dialogMessage: {
          isOpen: false,
          title: "Information",
          message: "",
          status: StatusMsg.info,
          barrierDismissible: true,
        },
      });
    } catch (error) {
      AppLog.show({
        message: `[hideDialogMessage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  showDialogConfirm: async ({
    title = "Confirmation",
    message = "",
    barrierDismissible = false,
    data = null,
    onSubmit = () => {},
    titileButtonSubmit = "Yes, Continue",
    titileButtonCancel = "No, Cancel",
  }: Partial<{
    title: string;
    message: string;
    barrierDismissible: boolean;
    data: any;
    onSubmit: (data?: any) => void;
    titileButtonSubmit?: string;
    titileButtonCancel?: string;
  }> = {}) => {
    try {
      const { dialogConfirm } = get();
      let tmpDialogConfirm = { ...dialogConfirm };

      tmpDialogConfirm.isOpen = true;
      tmpDialogConfirm.title = title;
      tmpDialogConfirm.message = message;
      tmpDialogConfirm.barrierDismissible = barrierDismissible;
      tmpDialogConfirm.data = data;
      tmpDialogConfirm.onSubmit = onSubmit;
      tmpDialogConfirm.titileButtonSubmit = titileButtonSubmit;
      tmpDialogConfirm.titileButtonCancel = titileButtonCancel;

      set({
        dialogConfirm: tmpDialogConfirm,
      });
    } catch (error) {
      AppLog.show({
        message: `[showDialogConfirm]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  hideDialogConfirm: async () => {
    try {
      set({
        dialogConfirm: {
          isOpen: false,
          title: "Confirmation",
          message: "",
          barrierDismissible: false,
          data: null,
          onSubmit: () => {},
          titileButtonSubmit: "Yes, Continue",
          titileButtonCancel: "No, Cancel",
        },
      });
    } catch (error) {
      AppLog.show({
        message: `[hideDialogConfirm]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Sound Controll ==== * //

  isMuted: getStoredValue("isMuted", false),
  volume: getStoredValue("volume", 1),

  toggleMute: async () => {
    try {
      const currentMuted = get().isMuted;
      set({ isMuted: !currentMuted });
      if (typeof window !== "undefined") {
        localStorage.setItem("isMuted", JSON.stringify(!currentMuted));
      }
    } catch (error) {
      AppLog.show({
        message: `[toggleMute]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setGlobalVolume: async (newVolume: number) => {
    try {
      set({ volume: newVolume, isMuted: newVolume === 0 });
      if (typeof window !== "undefined") {
        localStorage.setItem("volume", JSON.stringify(newVolume));
        localStorage.setItem("isMuted", JSON.stringify(newVolume === 0));
      }
    } catch (error) {
      AppLog.show({
        message: `[setGlobalVolume]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Theme ==== * //

  theme: getThemeValue(),

  toggleTheme: async () => {
    try {
      const { theme } = get();

      const newTheme: Theme = theme === "dark" ? "light" : "dark";

      set({ theme: newTheme });

      document.documentElement.classList.remove(theme);
      document.documentElement.classList.add(newTheme);

      localStorage.setItem("theme", newTheme);
    } catch (error) {
      AppLog.show({
        message: `[toggleMute]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Upload Post Content ==== * //

  dialogUploadPostContent: {
    isOpen: false,
    type: UploadPostContentType.photoVideo,
  },

  showDialogUploadPostContent: async (uploadPostContentType: UploadPostContentType) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      const { dialogUploadPostContent } = get();
      let tmpDialogUploadPostContent = { ...dialogUploadPostContent };
      tmpDialogUploadPostContent.isOpen = true;
      tmpDialogUploadPostContent.type = uploadPostContentType;

      set({
        dialogUploadPostContent: tmpDialogUploadPostContent,
      });
    } catch (error) {
      AppLog.show({
        message: `[showDialogUploadPostContent]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  hideDialogUploadPostContent: async () => {
    try {
      set({
        dialogUploadPostContent: {
          isOpen: false,
          type: UploadPostContentType.photoVideo,
        },
      });
    } catch (error) {
      AppLog.show({
        message: `[hideDialogUploadPostContent]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Upload Edit Post Content ==== * //

  dialogUploadEditPostContent: {
    isOpen: false,
    postEdit: null,
    onSuccess: (_) => {},
  },

  showDialogUploadEditPostContent: async (postEdit: any, onSuccess: (post: any) => void) => {
    try {
      if (Service.isAuthenticated === false) {
        appStore.getState().onOpenDialogWelcome();
        return;
      }

      const { dialogUploadEditPostContent } = get();
      let tmpDialogUploadEditPostContent = { ...dialogUploadEditPostContent };
      tmpDialogUploadEditPostContent.isOpen = true;
      tmpDialogUploadEditPostContent.postEdit = postEdit;
      tmpDialogUploadEditPostContent.onSuccess = onSuccess;

      set({
        dialogUploadEditPostContent: tmpDialogUploadEditPostContent,
      });
    } catch (error) {
      AppLog.show({
        message: `[showDialogUploadEditPostContent]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  hideDialogUploadEditPostContent: async () => {
    try {
      set({
        dialogUploadEditPostContent: {
          isOpen: false,
          postEdit: null,
          onSuccess: (_) => {},
        },
      });
    } catch (error) {
      AppLog.show({
        message: `[hideDialogUploadEditPostContent]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Dailog Modal Post ==== * //

  dialogPostModal: {
    isOpen: false,
    postId: null,
    tmpRouteUrl: null,
  },

  onOpenDialogPostModal: async (postId: string, tmpRouteUrl: string) => {
    try {
      set({
        dialogPostModal: {
          postId: postId,
          isOpen: true,
          tmpRouteUrl: tmpRouteUrl,
        },
      });
      window.history.pushState({}, "", `/post?post_id=${postId}`);
      document.body.style.overflow = "hidden";
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogPostModal]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onCloseDialogPostModal: async () => {
    try {
      const { dialogPostModal } = get();
      window.history.pushState({}, "", dialogPostModal.tmpRouteUrl);
      document.body.style.overflow = "";
      set({
        dialogPostModal: { postId: null, isOpen: false, tmpRouteUrl: null },
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogPostModal]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Dialog Welcome ==== * //
  dialogWelcome: {
    isOpen: false,
  },

  onOpenDialogWelcome: async () => {
    try {
      set({
        dialogWelcome: {
          isOpen: true,
        },
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogWelcome]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onCloseDialogWelcome: async () => {
    try {
      set({
        dialogWelcome: { isOpen: false },
      });
    } catch (error) {
      AppLog.show({
        message: `[onCloseDialogWelcome]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ==== Dialog Welcome ==== * //
  dialogViewStoryModal: {
    isOpen: false,
    userId: null,
    tmpRouteUrl: null,
  },

  onOpenDialogViewStoryModal: async (userId: string, tmpRouteUrl: string) => {
    try {
      set({
        dialogViewStoryModal: {
          userId: userId,
          isOpen: true,
          tmpRouteUrl: tmpRouteUrl,
        },
      });
      window.history.pushState({}, "", `/view-stories?user_id=${userId}`);
      document.body.style.overflow = "hidden";
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogViewStoryModal]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onCloseDialogViewStoryModal: async () => {
    try {
      const { dialogViewStoryModal } = get();
      window.history.pushState({}, "", dialogViewStoryModal.tmpRouteUrl);
      document.body.style.overflow = "";
      set({
        dialogViewStoryModal: { userId: null, isOpen: false, tmpRouteUrl: null },
      });
    } catch (error) {
      AppLog.show({
        message: `[onCloseDialogViewStoryModal]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

export default appStore;

